import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Download, Trash2, CheckCircle2, Send, Loader2, FileText, Undo2, Save,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { REPORT_STATUSES, fmtN, fmtPct } from "./AnalyseBlocks";

const API = process.env.REACT_APP_BACKEND_URL;

function KpiTable({ stats }) {
  const s = stats?.social || {};
  const t = s.totals || {}, p = s.prev_totals || {};
  const delta = (a, b) => (b ? `${((a - b) / b * 100).toFixed(0)} %` : "—");
  const rows = [
    ["Abonnés (tous réseaux)", t.followers, p.followers],
    ["Nouveaux abonnés", t.new_followers, p.new_followers],
    ["Portée", t.reach, p.reach],
    ["Impressions", t.impressions, p.impressions],
    ["Interactions", t.interactions, p.interactions],
    ["Taux d'engagement", s.engagement_rate != null ? fmtPct(s.engagement_rate) : "—", p.reach ? fmtPct(Math.round(p.interactions / p.reach * 1000) / 10) : "—"],
    ["Clics lien", t.clicks, p.clicks],
    ["Conversions", t.conversions, p.conversions],
    ["Publications", stats?.pubs_total, stats?.prev_pubs_total],
  ];
  return (
    <table className="w-full text-[12px]" data-testid="rapport-kpi-table">
      <thead>
        <tr className="text-[9px] font-semibold uppercase tracking-wider text-neutral-400 border-b border-neutral-100">
          <th className="py-2 text-left">Métrique</th>
          <th className="py-2 text-right">Période</th>
          <th className="py-2 text-right">Période précédente</th>
          <th className="py-2 text-right">Évolution</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([l, cur, prev]) => (
          <tr key={l} className="border-b border-neutral-50 last:border-0">
            <td className="py-2 text-neutral-600">{l}</td>
            <td className="py-2 text-right font-semibold text-[#0F172A]">{typeof cur === "string" ? cur : fmtN(cur)}</td>
            <td className="py-2 text-right text-neutral-400">{typeof prev === "string" ? prev : fmtN(prev)}</td>
            <td className="py-2 text-right text-neutral-500">{typeof cur === "string" ? "—" : delta(cur || 0, prev || 0)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function RapportView() {
  const { rid } = useParams();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [note, setNote] = useState("");
  const [insights, setInsights] = useState("");
  const [busy, setBusy] = useState("");
  const [notify, setNotify] = useState(true);

  const load = () => api.get(`/reports/${rid}`).then((res) => {
    setR(res.data);
    setNote(res.data.note_agence || "");
    setInsights(res.data.insights || "");
  }).catch((e) => { toast.error(apiError(e)); navigate("/cm?tab=analyse&sub=rapports"); });
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [rid]);

  if (!r) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-neutral-300" /></div>;
  }
  const st = REPORT_STATUSES[r.status] || REPORT_STATUSES.genere;
  const locked = r.status === "publie";
  const social = r.stats?.social || {};

  const act = async (key, fn, msg) => {
    setBusy(key);
    try { await fn(); if (msg) toast.success(msg); await load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };
  const save = () => act("save", () => api.patch(`/reports/${rid}`, { note_agence: note, insights }), "Rapport enregistré");
  const validate = () => act("valide", () => api.patch(`/reports/${rid}`, { status: "valide" }), "Rapport validé");
  const publish = () => {
    if (!window.confirm(notify
      ? "Publier ce rapport dans l'espace client ET notifier le client par email ?"
      : "Publier ce rapport dans l'espace client (sans email) ?")) return;
    act("publie", () => api.post(`/reports/${rid}/publish`, { notify }),
      "Rapport publié — visible dans « Mes documents » de l'espace client");
  };
  const unpublish = () => {
    if (!window.confirm("Retirer ce rapport de l'espace client ?")) return;
    act("retire", () => api.post(`/reports/${rid}/unpublish`), "Rapport retiré de l'espace client");
  };
  const remove = () => {
    if (!window.confirm("Supprimer définitivement ce rapport ?")) return;
    act("suppr", async () => { await api.delete(`/reports/${rid}`); navigate("/cm?tab=analyse&sub=rapports"); }, "Rapport supprimé");
  };

  return (
    <div className="space-y-3 max-w-5xl" data-testid="rapport-view">
      <button onClick={() => navigate("/cm?tab=analyse&sub=rapports")} data-testid="rapport-back"
        className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-neutral-500 hover:text-neutral-900 transition-colors">
        <ArrowLeft className="w-3.5 h-3.5" /> Retour à l'analyse
      </button>

      {/* En-tête */}
      <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm px-5 py-4 flex flex-wrap items-center gap-3" data-testid="rapport-header">
        <span className="w-10 h-10 rounded-xl bg-[#0B132B] flex items-center justify-center shrink-0">
          <FileText className="w-5 h-5 text-white" />
        </span>
        <div className="min-w-0">
          <h1 className="font-heading text-lg font-bold text-[#0F172A] capitalize">
            {r.client_name} — <span className="normal-case">Rapport {r.period_label}</span>
          </h1>
          <p className="text-[11px] text-neutral-400">
            Créé le {r.created_at ? new Date(r.created_at).toLocaleDateString("fr-FR") : "—"} par {r.created_by}
            {r.auto && " · génération automatique"}
            {r.published_at && ` · publié le ${new Date(r.published_at).toLocaleDateString("fr-FR")}`}
          </p>
        </div>
        <span className={`text-[10px] font-semibold rounded-full px-2.5 py-1 border ${st.cls}`} data-testid="rapport-status">{st.label}</span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <a href={`${API}/api/reports/${r.id}/pdf`} target="_blank" rel="noreferrer" data-testid="rapport-pdf"
            className="inline-flex items-center gap-1.5 border border-[#E2E8F0] rounded-xl px-3.5 py-2 text-xs font-semibold text-[#0F172A] hover:bg-neutral-50 transition-colors">
            <Download className="w-3.5 h-3.5" /> Télécharger PDF
          </a>
          {!locked && (
            <button onClick={save} disabled={!!busy} data-testid="rapport-save"
              className="inline-flex items-center gap-1.5 border border-[#E2E8F0] rounded-xl px-3.5 py-2 text-xs font-semibold text-[#0F172A] hover:bg-neutral-50 transition-colors disabled:opacity-50">
              {busy === "save" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Enregistrer
            </button>
          )}
          {!locked && r.status !== "valide" && (
            <button onClick={validate} disabled={!!busy} data-testid="rapport-validate"
              className="inline-flex items-center gap-1.5 border border-green-200 bg-green-50 rounded-xl px-3.5 py-2 text-xs font-semibold text-green-700 hover:bg-green-100 transition-colors disabled:opacity-50">
              <CheckCircle2 className="w-3.5 h-3.5" /> Valider
            </button>
          )}
          {!locked && (
            <button onClick={publish} disabled={!!busy} data-testid="rapport-publish"
              className="inline-flex items-center gap-1.5 bg-[#0B132B] text-white rounded-xl px-4 py-2 text-xs font-semibold hover:bg-[#1C2A4F] transition-colors disabled:opacity-50">
              {busy === "publie" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {r.status === "valide" ? "Publier au client" : "Valider et publier"}
            </button>
          )}
          {locked && (
            <button onClick={unpublish} disabled={!!busy} data-testid="rapport-unpublish"
              className="inline-flex items-center gap-1.5 border border-amber-200 bg-amber-50 rounded-xl px-3.5 py-2 text-xs font-semibold text-amber-700 hover:bg-amber-100 transition-colors disabled:opacity-50">
              <Undo2 className="w-3.5 h-3.5" /> Retirer de l'espace client
            </button>
          )}
          <button onClick={remove} disabled={!!busy} data-testid="rapport-delete"
            className="inline-flex items-center gap-1.5 border border-red-200 text-red-600 rounded-xl px-3 py-2 text-xs font-semibold hover:bg-red-50 transition-colors disabled:opacity-50">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 space-y-3">
          {/* Résumé exécutif (éditable avant publication) */}
          <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-5" data-testid="rapport-insights-card">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400 mb-2">
              Résumé exécutif (IA){!locked && " — modifiable avant publication"}
            </p>
            {locked
              ? <p className="text-[13px] text-neutral-700 whitespace-pre-line leading-relaxed" data-testid="rapport-insights">{r.insights}</p>
              : <textarea value={insights} onChange={(e) => setInsights(e.target.value)} rows={10} data-testid="rapport-insights-edit"
                  className="w-full text-[13px] text-neutral-700 leading-relaxed border border-[#E2E8F0] rounded-xl px-3 py-2.5 focus:outline-none focus:border-[#94A3B8]" />}
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-5" data-testid="rapport-kpis">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400 mb-2">
              Chiffres clés {social.comparison_label && <span className="normal-case font-normal">— comparaison : {social.comparison_label}</span>}
            </p>
            <KpiTable stats={r.stats} />
            {social.demo && <p className="text-[10px] text-amber-600 mt-2">Statistiques sociales de démonstration (comptes Meta non connectés).</p>}
          </div>

          {social.top_publications?.length > 0 && (
            <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-5" data-testid="rapport-top">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400 mb-2">Top publications</p>
              {social.top_publications.map((t, i) => (
                <div key={i} className="flex items-center gap-3 py-2 border-b border-neutral-50 last:border-0 text-[12px]">
                  <span className="flex-1 min-w-0 truncate font-medium text-[#0F172A]">{t.title}</span>
                  <span className="text-neutral-400 shrink-0">{t.format}</span>
                  <span className="text-neutral-500 shrink-0">Portée {fmtN(t.reach)}</span>
                  <span className="text-neutral-500 shrink-0">{fmtN(t.likes)} j'aime</span>
                </div>
              ))}
            </div>
          )}

          {social.objectives?.some((o) => o.target > 0) && (
            <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-5" data-testid="rapport-objectifs">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400 mb-3">Objectifs de la période</p>
              {social.objectives.filter((o) => o.target > 0).map((o) => (
                <div key={o.id} className="mb-3">
                  <div className="flex justify-between text-[11px]">
                    <span className="font-medium text-[#475569]">{o.label}</span>
                    <span className="font-bold text-[#0F172A]">{o.pct} %</span>
                  </div>
                  <div className="h-2 rounded-full bg-neutral-100 mt-1 overflow-hidden">
                    <div className={`h-full rounded-full ${o.pct >= 100 ? "bg-[#15803D]" : "bg-[#0369A1]"}`} style={{ width: `${Math.min(100, o.pct)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-3">
          {/* Note agence */}
          <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-5" data-testid="rapport-note-card">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400 mb-2">
              Note de l'agence {!locked && "— visible par le client"}
            </p>
            {locked
              ? <p className="text-[12px] text-neutral-600 whitespace-pre-line">{r.note_agence || "Aucune note."}</p>
              : <>
                  <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} data-testid="rapport-note-edit"
                    placeholder="Ex : Ce mois-ci, la progression des Reels est particulièrement intéressante…"
                    className="w-full text-[12px] text-neutral-700 border border-[#E2E8F0] rounded-xl px-3 py-2.5 focus:outline-none focus:border-[#94A3B8]" />
                  <p className="text-[10px] text-neutral-400 mt-1">Pensez à cliquer « Enregistrer » avant de publier.</p>
                </>}
          </div>

          {/* Publication */}
          {!locked && (
            <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-5" data-testid="rapport-publish-card">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400 mb-2">Publication au client</p>
              <label className="flex items-center gap-2 text-[12px] text-neutral-600 cursor-pointer">
                <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="rapport-notify"
                  className="rounded border-neutral-300" />
                Notifier le client par email à la publication
              </label>
              <p className="text-[10px] text-neutral-400 mt-2 leading-snug">
                Le rapport (PDF + version consultable) apparaîtra dans <strong>Espace client → Mes documents</strong>,
                catégorie « Rapport Community Management ».
              </p>
              <p className="text-[10px] text-neutral-400 mt-1.5 leading-snug">
                Astuce : le bouton <strong>Exporter</strong> de la vue Statistiques client génère le PDF visuel
                (page 1 = capture exacte de l'écran d'analyse, page 2 = analyse & recommandations) — il est alors
                joint automatiquement à la publication.
              </p>
            </div>
          )}

          {/* Méta */}
          <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-5 text-[12px] space-y-1.5" data-testid="rapport-meta">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400 mb-1">Détails</p>
            <p className="text-neutral-600">Période : <strong className="capitalize">{r.period_label}</strong></p>
            <p className="text-neutral-600">Type : {r.period === "3m" ? "3 mois" : r.period === "6m" ? "6 mois" : "Mensuel"}</p>
            <p className="text-neutral-600">Publications : {r.stats?.pubs_total ?? "—"} dont {r.stats?.pubs_published ?? 0} publiées</p>
            {r.stats?.time_minutes != null && (
              <p className="text-neutral-600">Temps agence : {Math.floor(r.stats.time_minutes / 60)} h {String(r.stats.time_minutes % 60).padStart(2, "0")}</p>
            )}
            {r.stats?.instagram && (
              <p className="text-neutral-600">Instagram @{r.stats.instagram.username} — {fmtN(r.stats.instagram.followers)} abonnés (données réelles)</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
